import { updateIntl } from '../../../components/IntlRedux';

import API from '../../../helpers/api';
import { setCookie } from '../../../helpers/cookie';
import { needsI18nLoad, toIntlLocale } from '../../../helpers/lang';

import DATA from '../actionTypes';

import { AppDispatch, GetState } from '../../../types/app';
import { BrokerType, ConfigType } from '../types';

export const prepare = () => (_dispatch: AppDispatch, getState: GetState) => {
  if (__SERVER__) {
    const {
      Data: { location, prefix, userAgent, cookie },
    } = getState();
    API.setLocation(location);
    API.setPrefix(prefix);
    API.setUserAgent(userAgent);
    API.setCookie(cookie);
  }
};

export const load = () => async (dispatch: AppDispatch) => {
  dispatch({ type: DATA.LOAD.REQUESTING });

  try {
    const { config, broker } = await API.callGraphQL<{
      config: ConfigType;
      broker: BrokerType;
    }>(
      `
      query ($app: ThemeApp)  {
      broker: dataBroker {
          Id
        }
        config: dataConfig(App: $app)
      }
    `,
      {
        app: 'ai',
      }
    );

    API.setProfileHost(config.profileHost);

    await dispatch({
      type: DATA.LOAD.SUCCESS,
      config,
      broker,
    });
  } catch (err) {
    console.error(err);
    await dispatch({ type: DATA.LOAD.FAILURE, err: err.message });
  }
};

export const loadIfNeeded =
  () => async (dispatch: AppDispatch, getState: GetState) => {
    const {
      Data: { config },
    } = getState();

    if (!config?.profileHost) {
      return dispatch(load());
    }

    API.setProfileHost(config?.profileHost);

    return null;
  };

export const loadLang =
  (lang: string) => async (dispatch: AppDispatch, getState: GetState) => {
    const {
      Data: { config },
    } = getState();

    const intlLocale = toIntlLocale(lang);
    const messages = await API.loadI18n(intlLocale, config?.staticVersion);

    if (!messages || !Object.keys(messages).length) {
      return false;
    }

    dispatch(
      updateIntl({
        defaultLocale: intlLocale,
        locale: intlLocale,
        messages,
      })
    );

    setCookie('lang', intlLocale);
    return true;
  };

export const loadLangIfNeeded =
  () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void | null> => {
    const {
      Data: { broker },
      Client: { data, isLoggedIn },
      intl: { locale, messages },
    } = getState();

    const sourceLang = isLoggedIn
      ? data?.Lang || locale
      : locale || broker?.Lang;
    const intlLocale = toIntlLocale(sourceLang);

    if (!needsI18nLoad(messages) && intlLocale === locale) {
      return null;
    }

    return dispatch(loadLang(sourceLang));
  };
